import { Fragment, useMemo } from "react"
import { CogoBlock } from "./cogo-block"
import type { CogoBlockDef, CogoElement, CogoPage as CogoPageDef } from "../core/types"

export interface CogoPageProps {
  page: CogoPageDef
  blocks: Record<string, CogoBlockDef>
  navigationType?: string
  /** Override default block rendering. Default: <CogoBlock block={blockDef} /> */
  renderBlock?: (blockName: string, blockDef: CogoBlockDef, index: number) => React.ReactNode
  /** When true, sidebar uses contained height instead of h-screen (for device frames). */
  contained?: boolean
}

function defaultRenderBlock(_blockName: string, blockDef: CogoBlockDef): React.ReactNode {
  return <CogoBlock block={blockDef} />
}

/** Find an element by ID in the tree (DFS). */
function findElementById(el: CogoElement, id: string): CogoElement | undefined {
  if (el.id === id) return el
  for (const child of el.children ?? []) {
    const found = findElementById(child, id)
    if (found) return found
  }
  return undefined
}

export function CogoPage({ page, blocks, navigationType, renderBlock = defaultRenderBlock, contained }: CogoPageProps) {
  const layout = page.layout as string | undefined

  // Sidebar layout: explicit page.layout or app-level navigation.type
  const useSidebar = layout === "sidebar" || navigationType === "sidebar"
  if (useSidebar) {
    return <SidebarLayout page={page} blocks={blocks} renderBlock={renderBlock} contained={contained} />
  }

  // Layout block wrapping: page.layout references a block name that wraps page content.
  // Clone the layout block and inject page blocks as block-ref children into its "content-body" slot.
  if (layout && layout !== "sidebar" && blocks[layout]) {
    return <LayoutBlockPage layoutBlock={blocks[layout]} page={page} blocks={blocks} />
  }

  return (
    <div data-page-id={page.id} className="overflow-x-hidden max-w-[100vw] w-full">
      {(page.blocks ?? []).map((blockName, index) => {
        const blockDef = blocks[blockName]
        if (!blockDef) {
          console.warn(`Block not found: ${blockName}`)
          return null
        }
        // Skip logic-only blocks (they have no UI to render)
        if (blockDef.manifest?.type === "logic") {
          return null
        }
        // Header blocks render full-width (sticky); other blocks get page container
        const root = blockDef.root
        const isHeader = root?.variant === "header"
          || root?.tag === "header"
          || root?.position?.type === "sticky"
        if (isHeader) {
          return <Fragment key={blockName}>{renderBlock(blockName, blockDef, index)}</Fragment>
        }
        return (
          <main key={blockName} className="overflow-x-hidden">
            {renderBlock(blockName, blockDef, index)}
          </main>
        )
      })}
    </div>
  )
}

interface SidebarLayoutProps extends CogoPageProps {
  renderBlock: NonNullable<CogoPageProps["renderBlock"]>
}

function SidebarLayout({ page, blocks, renderBlock, contained }: SidebarLayoutProps) {
  // Partition blocks into sidebar vs content
  type BlockEntry = { name: string; def: CogoBlockDef; index: number }
  const sidebarBlocks: BlockEntry[] = []
  const headerBlocks: BlockEntry[] = []
  const contentBlocks: BlockEntry[] = []

  page.blocks.forEach((blockName, index) => {
    const blockDef = blocks[blockName]
    if (!blockDef) return
    if (blockDef.manifest?.type === "logic") return

    const root = blockDef.root
    const isSidebar = blockName.includes("sidebar")
      || root?.variant === "sidebar"
      || root?.tag === "aside"
    const isHeader = root?.variant === "header"
      || root?.tag === "header"
      || root?.position?.type === "sticky"

    const entry: BlockEntry = { name: blockName, def: blockDef, index }
    if (isSidebar) sidebarBlocks.push(entry)
    else if (isHeader) headerBlocks.push(entry)
    else contentBlocks.push(entry)
  })

  return (
    <div data-page-id={page.id} className={`flex ${contained ? "h-full min-h-[600px]" : "h-screen"} overflow-hidden`}>
      {/* Sidebar — fixed width, full height, scrollable */}
      {sidebarBlocks.length > 0 && (
        <aside className="hidden w-[220px] shrink-0 overflow-y-auto border-r bg-card md:block">
          {sidebarBlocks.map(({ name, def, index }) => (
            <Fragment key={name}>{renderBlock(name, def, index)}</Fragment>
          ))}
        </aside>
      )}
      {/* Main area — flex column for header + scrollable content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header blocks — sticky at top */}
        {headerBlocks.map(({ name, def, index }) => (
          <Fragment key={name}>{renderBlock(name, def, index)}</Fragment>
        ))}
        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-7xl p-6">
            {contentBlocks.map(({ name, def, index }) => (
              <Fragment key={name}>{renderBlock(name, def, index)}</Fragment>
            ))}
          </div>
        </main>
      </div>
    </div>
  )
}

/**
 * Renders a page wrapped in a layout block.
 * Clones the layout block's element tree and injects page blocks as block-ref
 * children into the element with id "content-body".
 */
function LayoutBlockPage({ layoutBlock, page, blocks }: {
  layoutBlock: CogoBlockDef
  page: CogoPageDef
  blocks: Record<string, CogoBlockDef>
}) {
  const modifiedLayout = useMemo(() => {
    const cloned: CogoBlockDef = JSON.parse(JSON.stringify(layoutBlock))
    const slot = findElementById(cloned.root, "content-body")
    if (slot) {
      slot.children = page.blocks.map((blockName) => ({
        id: `layout-slot-${blockName}`,
        type: "block-ref" as const,
        blockType: blockName,
      }))
    }
    return cloned
  }, [layoutBlock, page.blocks])

  return (
    <div data-page-id={page.id}>
      <CogoBlock block={modifiedLayout} />
    </div>
  )
}
